import { fetchBulkPayoutFee } from '@api';
import { ReactComponent as Add } from '@basqet-dashboard/common/assets/icons/add-large.svg';
import { ReactComponent as UploadIcon } from '@basqet-dashboard/common/assets/icons/upload.svg';
import { Button, Dropdown } from '@basqet-dashboard/common/components';
import {
  bulkTransferSchema,
  capitalize,
  formatErrorMessage,
  useNavigateTo
} from '@basqet-dashboard/common/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFetchBeneficiaries } from '@hooks/queries';
import { BulkBeneficiaryModal } from '@pages/beneficiary';
import { BackButton } from '@pages/payout/back-button';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

const AddBeneficiaries = ({ transactionInfo, setCurrentStage, setTransactionInfo }) => {
  const { beneficiaries } = useFetchBeneficiaries({ grouped: true });
  const beneficiariesOptions = beneficiaries.map((beneficiary) => ({
    value: beneficiary.label,
    label: `${capitalize(beneficiary.label)} - ${beneficiary.row_count} Row(s)`
  }));

  const { navigateTo } = useNavigateTo();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: { ...transactionInfo },
    resolver: yupResolver(bulkTransferSchema)
  });

  const [isSelectBeneficiaries, setIsSelectBeneficiaries] = useState(Boolean(transactionInfo));
  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);

  const onSubmit = async (data) => {
    const { beneficiaries_label } = data;
    const beneficiary = beneficiaries.find(
      (beneficiary) => beneficiary.label === beneficiaries_label
    );
    await fetchFeeAndContinue(beneficiary);
  };

  const closeUploadModal = () => {
    setIsOpenUploadModal(false);
  };

  const fetchFeeAndContinue = async (txInfo) => {
    try {
      const label = txInfo?.label ?? txInfo?.beneficiaries_label;

      const { data: response } = await fetchBulkPayoutFee({ label });
      const { amount, transactionFee, totalAmount } = response;
      setTransactionInfo({ ...txInfo, amount, fee: transactionFee, totalAmount });
      setCurrentStage('CONFIRM_PAYOUT');
    } catch (error) {
      formatErrorMessage(error);
    }
  };

  const uploadRecipients = async (info) => {
    if (info) {
      await fetchFeeAndContinue(info);
    }
    closeUploadModal(false);
  };

  return (
    <div className="card add__beneficiaries">
      <BackButton onClick={() => navigateTo('/payouts')} />
      <div className="card__header">
        <h3 className="card__title">
          Active <span className="text-cursive">Account</span>
        </h3>
      </div>
      <h4 className="add__beneficiaries__label">Add Recipients</h4>
      <div className="add__beneficiaries__actions">
        <button
          onClick={() => setIsOpenUploadModal(true)}
          className="add__beneficiaries__actions--item">
          <UploadIcon />
          <span>Upload New List</span>
        </button>
        <button
          onClick={() => setIsSelectBeneficiaries(true)}
          className="add__beneficiaries__actions--item">
          <Add />
          <span>Select Bulk beneficiaries</span>
        </button>
      </div>
      {isSelectBeneficiaries && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Dropdown
            placeholder="Bulk Beneficiary"
            name="beneficiaries_label"
            id="beneficiaries_label"
            options={beneficiariesOptions}
            defaultValue={transactionInfo?.label}
            register={register('beneficiaries_label')}
            error={errors?.beneficiaries_label?.message}
          />

          <div className="card__footer">
            <Button disabled={!isValid} isFull type="submit" isLoading={isSubmitting}>
              Continue
            </Button>
          </div>
        </form>
      )}

      {isOpenUploadModal && (
        <BulkBeneficiaryModal
          bulkRecipients={true}
          onModalClose={closeUploadModal}
          uploadRecipients={uploadRecipients}
        />
      )}
    </div>
  );
};
AddBeneficiaries.propTypes = {
  setCurrentStage: PropTypes.func,
  setTransactionInfo: PropTypes.func,
  transactionInfo: PropTypes.object
};

export { AddBeneficiaries };
